/* eslint-disable i18next/no-literal-string -- const enum values mirror the backend EntityType enum */

/**
 * Closed enumeration of every entity type that participates in cross-module
 * references via {@link EntityLink}. Mirrors the backend
 * {@code com.irian.iam.shared.api.EntityType} enum and is kept in sync by the
 * `RequireEnumTypescriptMatch` convention check.
 */
export const EntityType = {
    IDENTITY: 'IDENTITY',
    PARTY: 'PARTY',
    RESOURCE: 'RESOURCE',
    SYSTEM: 'SYSTEM',
    ENFORCEMENT_OBJECT: 'ENFORCEMENT_OBJECT',
    ACCESS_PROFILE: 'ACCESS_PROFILE',
    FUNCTION_GROUP: 'FUNCTION_GROUP',
    AUTH_USER: 'AUTH_USER',
    REPORT_CATEGORY: 'REPORT_CATEGORY',
    REPORT_EXECUTION: 'REPORT_EXECUTION',
    CONNECTOR: 'CONNECTOR',
} as const

export type EntityType = typeof EntityType[keyof typeof EntityType]

/**
 * Canonical entity-reference shape carried as a unit through every cross-module
 * reference — filter values, URL state, query keys, picker chips. Mirrors the
 * backend {@code com.irian.iam.shared.api.EntityLink} record.
 *
 * Returned by every {@code GET /api/i/{entity}/links} endpoint.
 *
 * - {@link displayName} — projected from the entity (e.g. `Identity.displayName`,
 *   `TargetSystem.name`); never null.
 * - {@link entityType} — drives polymorphic chip rendering and detail-page routing.
 * - {@link subType} — module-owned type-table code (`'WORKFORCE'`, `'PERSON'`, …);
 *   `null` when the entity has no type table (AuthUser, ReportCategory).
 * - {@link code} — stable technical identifier (AccessProfile.code, Resource.nativeKey);
 *   `null` when the entity has no separate code field (Identity, Party, FunctionGroup,
 *   AuthUser). Rendered alongside the displayName in chips for human recognition.
 */
export interface EntityLink {
    readonly id: string
    readonly displayName: string
    readonly entityType: EntityType
    readonly subType: string | null
    readonly code: string | null
}
